import { Fragment, useMemo, useState, type CSSProperties } from 'react';
import { CheckCircle2, Plus, X } from 'lucide-react';
import {
  ChartEmpty,
  SEVERITY_BG,
  SEVERITY_LABEL,
  SEVERITY_ORDER,
  SeverityByDomain,
  SeverityLegend,
  useEntryMotion,
  type Severity,
} from '@/components/ui/charts';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { StatusChip, type StatusChipTone } from '@/components/ui/status-chip';
import { Callout, FilterChip, SegmentedControl, WorkspaceButton } from '@/components/ui/workspace';
import { useHealthReadinessQuery, type HealthReadinessView } from '@/lib/api/health-readiness';
import {
  CLOSED_FINDING_STATUSES,
  extractFindingPayloadString,
  useFindingListQuery,
  useUpdateFindingMutation,
  type FindingListItem,
} from '@/lib/api/health-findings';
import { cn } from '@/lib/utils/cn';
import { plural } from '../model';
import { useHrNavigate } from '../navigation';
import { severityRows } from '../overview/overview-model';
import { CountUp, OverviewCard } from '../overview/parts';
import { FindingForm } from './finding-form';
import {
  STATUS_FILTERS,
  filtersFromSearch,
  findingAgeDays,
  listQuery,
  medianOpenAge,
  severityOf,
  sortFindings,
  type StatusFilter,
} from './findings-model';
import { humanize } from './pane-header';
import { Td, Th } from './table-cells';
import { usePaneSearch } from './use-pane-search';

/**
 * Health & Readiness → Findings (T-0244 / E22 design §4; prototype
 * `findingsTab()`), top to bottom: the KPI row (open, critical, high,
 * median age, resolved), open findings by domain and severity (a segment
 * filters the register), the filter row (status, severity, the domain
 * chip, New finding), and the register with each finding's age, where a
 * row resolves, reopens or opens for edit.
 *
 * The filters live in the URL (`…/findings?status=&severity=&domain=`,
 * open findings by default), so a filtered view can be shared. The counts
 * are the composite read's; the median age is the one statistic read off
 * the findings list (`findings-model.ts`). Every write goes through
 * `PATCH …/health/findings/:id` (audited by the API) and refreshes the
 * composite, so the KPIs follow.
 */

const LIST_LIMIT = 200;

const STATUS_LABEL: Record<StatusFilter, string> = {
  open: 'Open',
  resolved: 'Resolved',
  waived: 'Waived',
  all: 'All',
};

const STATUS_TONE: Record<string, StatusChipTone> = {
  open: 'at-risk',
  acknowledged: 'at-risk',
  remediating: 'at-risk',
  resolved: 'healthy',
  waived: 'neutral',
};

type Editing = { kind: 'new' } | { kind: 'edit'; id: string } | null;

export function FindingsPane({
  engagementId,
  today = new Date(),
}: {
  engagementId: string;
  /** Today, for each finding's age. */
  today?: Date;
}) {
  const { data: view } = useHealthReadinessQuery(engagementId);
  const { stringParam, update } = usePaneSearch();
  const filters = filtersFromSearch({
    status: stringParam('status'),
    severity: stringParam('severity'),
    domain: stringParam('domain'),
  });
  const list = useFindingListQuery(engagementId, listQuery(filters, LIST_LIMIT));
  // Every finding, whatever the filters: the median age and "none yet".
  const everything = useFindingListQuery(
    engagementId,
    listQuery({ status: 'all', severity: '', domain: '' }, LIST_LIMIT),
  );
  const patch = useUpdateFindingMutation(engagementId);
  const go = useHrNavigate(engagementId);
  const { animate } = useEntryMotion('findings');

  const [editing, setEditing] = useState<Editing>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const domainName = useMemo(() => {
    const names = new Map<string, string>();
    for (const d of view?.retiredDomains ?? []) names.set(d.id, `${d.name} (retired)`);
    for (const d of view?.domains ?? []) names.set(d.id, d.name);
    return names;
  }, [view]);

  const rows = useMemo(() => sortFindings(list.data?.items ?? [], today), [list.data, today]);
  if (!view) return null;

  // Filters replace the history entry: Back leaves the tab, it does not
  // step through every filter click.
  const setFilters = (patchSearch: Record<string, string | undefined>) =>
    update(patchSearch, { replace: true });

  const startNew = () => {
    setNotice(null);
    setEditing((e) => (e?.kind === 'new' ? null : { kind: 'new' }));
  };

  const setStatus = (row: FindingListItem, status: string, done: string) => {
    setNotice(null);
    patch.mutate(
      { findingId: row.id, body: { status } },
      { onSuccess: () => setNotice(`${done}: ${titleOf(row)}.`) },
    );
  };

  const none = everything.data?.items.length === 0;
  if (none && editing?.kind !== 'new') {
    return (
      <div data-testid="findings-pane">
        <ChartEmpty title="No findings yet" className="py-14">
          Findings come from interviews, document reviews and your own observations.
          <div className="mt-3">
            <WorkspaceButton variant="primary" onClick={startNew}>
              <Plus className="size-3.5" aria-hidden="true" />
              New finding
            </WorkspaceButton>
          </div>
        </ChartEmpty>
      </div>
    );
  }

  const maxAge = Math.max(30, ...rows.map((r) => findingAgeDays(r, today) ?? 0));
  const segmentStatus = (STATUS_FILTERS as readonly string[]).includes(filters.status)
    ? (filters.status as StatusFilter)
    : null;

  return (
    <div className={cn('flex flex-col gap-4', animate && 'chart-anim')} data-testid="findings-pane">
      <KpiRow
        view={view}
        medianAge={everything.data ? medianOpenAge(everything.data.items, today) : null}
      />

      <OverviewCard
        title="Open findings by domain"
        aside={
          view.evidence.openBySeverity ? (
            <SeverityLegend counts={view.evidence.openBySeverity} />
          ) : null
        }
      >
        <SeverityByDomain
          rows={severityRows(view)}
          onSelect={(id, severity) =>
            setFilters({ domain: id || undefined, severity, status: undefined })
          }
          renderName={(row) =>
            row.id ? (
              <button
                type="button"
                onClick={() => setFilters({ domain: row.id, severity: undefined })}
                className="max-w-full truncate text-left text-inherit hover:underline hover:underline-offset-[3px]"
              >
                {row.name}
              </button>
            ) : (
              row.name
            )
          }
        />
        {view.evidence.openFindings > 0 ? (
          <p className="mt-2 text-meta text-ink-500">Click a segment to filter the list below.</p>
        ) : null}
      </OverviewCard>

      <div
        role="toolbar"
        aria-label="Filter findings"
        className="flex flex-wrap items-center gap-2"
      >
        <SegmentedControl<StatusFilter>
          aria-label="Status"
          value={segmentStatus}
          onChange={(v) => setFilters({ status: v === 'open' ? undefined : v })}
          options={STATUS_FILTERS.map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
        />
        <SegmentedControl<Severity | 'any'>
          aria-label="Severity"
          value={severityOf(filters.severity) ?? (filters.severity ? null : 'any')}
          onChange={(v) => setFilters({ severity: v === 'any' ? undefined : v })}
          options={[
            { value: 'any', label: 'Any severity' },
            ...SEVERITY_ORDER.map((s) => ({
              value: s,
              label: (
                <span className="inline-flex items-center gap-1.5">
                  <SeverityDot severity={s} size={8} />
                  {SEVERITY_LABEL[s]}
                </span>
              ),
            })),
          ]}
        />
        {filters.domain ? (
          <FilterChip onClick={() => setFilters({ domain: undefined })}>
            <span className="sr-only">Clear the domain filter: </span>
            {domainName.get(filters.domain) ?? 'Retired domain'}
            <X className="size-3" aria-hidden="true" />
          </FilterChip>
        ) : null}
        <span className="text-meta text-ink-500">
          {list.data ? plural(rows.length, 'finding', 'findings') : ''}
        </span>
        <WorkspaceButton variant="primary" className="ml-auto" onClick={startNew}>
          <Plus className="size-3.5" aria-hidden="true" />
          New finding
        </WorkspaceButton>
      </div>

      {editing?.kind === 'new' ? (
        <FindingForm
          engagementId={engagementId}
          heading="New finding"
          submitLabel="Create finding"
          initial={filters.domain ? { domainId: filters.domain } : {}}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setNotice('Finding created. It is on the register and in the next report draft.');
          }}
        />
      ) : null}

      {notice ? (
        <Callout tone="ok" icon={CheckCircle2} role="status">
          {notice}
        </Callout>
      ) : null}
      {patch.error ? <ErrorMessage error={patch.error} /> : null}

      {list.isLoading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading findings…
        </div>
      ) : list.error ? (
        <ErrorMessage error={list.error} />
      ) : (
        <section className="overflow-x-auto rounded-card border border-ink-100 bg-paper-0">
          <table className="w-full border-collapse text-[13px] text-ink-700">
            <caption className="sr-only">Findings register</caption>
            <thead>
              <tr>
                <Th>Finding</Th>
                <Th>Domain</Th>
                <Th>Severity</Th>
                <Th>Type</Th>
                <Th className="text-right">Age</Th>
                <Th>Status</Th>
                <Th>
                  <span className="sr-only">Actions</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const age = findingAgeDays(row, today);
                const sev = severityOf(row.severity);
                const closed = CLOSED_FINDING_STATUSES.has(row.status);
                const source = extractFindingPayloadString(row.payload, 'source_kind');
                const title = titleOf(row);
                const isEditing = editing?.kind === 'edit' && editing.id === row.id;
                return (
                  <Fragment key={row.id}>
                    <tr className="group" data-testid={`finding-row-${row.id}`}>
                      <Td>
                        <span className="font-medium text-ink-900">{title}</span>
                        {source ? (
                          <span className="block text-meta text-ink-500">{humanize(source)}</span>
                        ) : null}
                      </Td>
                      <Td className="whitespace-nowrap">
                        {row.health_domain_id ? (
                          <button
                            type="button"
                            onClick={() => go({ tab: 'domains', id: row.health_domain_id! })}
                            className="text-left text-ink-900 underline hover:text-ink-700"
                          >
                            {domainName.get(row.health_domain_id) ?? 'Retired domain'}
                          </button>
                        ) : (
                          <span className="text-ink-300">No domain</span>
                        )}
                      </Td>
                      <Td>
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                          {sev ? <SeverityDot severity={sev} /> : null}
                          {sev ? SEVERITY_LABEL[sev] : row.severity ? humanize(row.severity) : '—'}
                        </span>
                      </Td>
                      <Td className="whitespace-nowrap">{row.category ?? '—'}</Td>
                      <Td className="text-right">
                        {age === null ? (
                          '—'
                        ) : (
                          <span className="inline-flex items-center justify-end gap-2">
                            <span className="tabular-nums">{age}d</span>
                            <span
                              aria-hidden="true"
                              className="relative block h-1.5 w-[60px] overflow-hidden rounded-[3px] bg-ink-100"
                            >
                              <i
                                className="absolute inset-y-0 left-0 bg-ink-500"
                                style={{ width: `${(age / maxAge) * 100}%` }}
                              />
                            </span>
                          </span>
                        )}
                      </Td>
                      <Td>
                        <StatusChip tone={STATUS_TONE[row.status] ?? 'neutral'} size="sm">
                          {humanize(row.status)}
                        </StatusChip>
                      </Td>
                      <Td className="whitespace-nowrap text-right">
                        <WorkspaceButton
                          variant="tertiary"
                          disabled={patch.isPending}
                          aria-label={`${closed ? 'Reopen' : 'Resolve'} ${title}`}
                          onClick={() =>
                            closed
                              ? setStatus(row, 'open', 'Reopened')
                              : setStatus(row, 'resolved', 'Resolved')
                          }
                        >
                          {closed ? 'Reopen' : 'Resolve'}
                        </WorkspaceButton>
                        <WorkspaceButton
                          variant="tertiary"
                          aria-label={`Edit ${title}`}
                          aria-expanded={isEditing}
                          onClick={() => {
                            setNotice(null);
                            setEditing(isEditing ? null : { kind: 'edit', id: row.id });
                          }}
                        >
                          Edit
                        </WorkspaceButton>
                      </Td>
                    </tr>
                    {isEditing ? (
                      <tr>
                        <td colSpan={7} className="border-b border-ink-100 bg-paper-50 p-3">
                          <FindingForm
                            engagementId={engagementId}
                            heading="Edit finding"
                            submitLabel="Save finding"
                            findingId={row.id}
                            initial={{
                              title,
                              type: row.category ?? undefined,
                              severity: row.severity ?? undefined,
                              domainId: row.health_domain_id ?? undefined,
                              status: row.status,
                              description:
                                extractFindingPayloadString(row.payload, 'description') ??
                                undefined,
                            }}
                            onCancel={() => setEditing(null)}
                            onSaved={() => {
                              setEditing(null);
                              setNotice(`Saved: ${title}.`);
                            }}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-ink-500">
                    No findings match these filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {list.data?.next_cursor ? (
            <p className="border-t border-ink-100 px-3 py-2 text-meta text-ink-500">
              Showing the first {LIST_LIMIT}; narrow the filters to see the rest.
            </p>
          ) : null}
        </section>
      )}
    </div>
  );
}

function titleOf(row: FindingListItem): string {
  return extractFindingPayloadString(row.payload, 'title') ?? '(untitled finding)';
}

/**
 * The KPI row (prototype `.kpi-row`): every count is the composite read's;
 * the median age is the findings list's.
 */
function KpiRow({ view, medianAge }: { view: HealthReadinessView; medianAge: number | null }) {
  const ev = view.evidence;
  const affected = ev.openByDomain.filter((r) => r.domainId !== null).length;
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
      <Kpi
        i={0}
        label="Open"
        value={ev.openFindings}
        sub={`${plural(affected, 'domain', 'domains')} affected`}
      />
      <Kpi i={1} label="Critical" value={ev.openBySeverity?.critical ?? null} sub="open" />
      <Kpi i={2} label="High" value={ev.openBySeverity?.high ?? null} sub="open" />
      <Kpi i={3} label="Median age" value={medianAge} sub="days open" />
      <Kpi i={4} label="Resolved" value={ev.resolvedFindings} sub="all time" />
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  i,
}: {
  label: string;
  value: number | null;
  sub: string;
  i: number;
}) {
  return (
    <section
      aria-label={label}
      className="a-rise flex flex-col gap-1.5 rounded-card border border-ink-100 bg-paper-0 px-5 py-4"
      style={{ '--i': i } as CSSProperties}
    >
      <span className="text-meta font-medium text-ink-500">{label}</span>
      <span
        className="text-[24px] font-semibold leading-[30px] tracking-[-0.01em] text-ink-900"
        data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {value === null ? '—' : <CountUp value={value} motionKey="findings" />}
      </span>
      <span className="text-meta text-ink-500">{sub}</span>
    </section>
  );
}

function SeverityDot({ severity, size = 10 }: { severity: Severity; size?: number }) {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block flex-none rounded-[3px]', SEVERITY_BG[severity])}
      style={{ width: size, height: size }}
    />
  );
}
